// ==================================
// Servicio de Veterinarias
// ==================================

import api from './api';
import type {
  Veterinaria,
  CrearVeterinariaInput,
  FiltrosVeterinarias,
  RespuestaPaginada,
  ApiResponse,
  LogoInfo,
} from '@/tipos';

export const veterinariasServicio = {
  listar: async (
    filtros: FiltrosVeterinarias = {}
  ): Promise<RespuestaPaginada<Veterinaria>> => {
    const params = new URLSearchParams();

    if (filtros.busqueda) params.append('busqueda', filtros.busqueda);
    if (filtros.estado) params.append('estado', filtros.estado);
    if (filtros.estadoLicencia) params.append('estadoLicencia', filtros.estadoLicencia);
    if (filtros.tipoLicencia) params.append('tipoLicencia', filtros.tipoLicencia);
    if (filtros.pagina) params.append('pagina', filtros.pagina.toString());
    if (filtros.limite) params.append('limite', filtros.limite.toString());

    const response = await api.get<ApiResponse<RespuestaPaginada<Veterinaria>>>(
      `/admin/veterinarias?${params.toString()}`
    );
    return response.data.data;
  },

  obtenerPorId: async (id: string): Promise<Veterinaria> => {
    const response = await api.get<ApiResponse<Veterinaria>>(
      `/admin/veterinarias/${id}`
    );
    return response.data.data;
  },

  crear: async (
    data: CrearVeterinariaInput
  ): Promise<{ veterinaria: Veterinaria; linkPagoKit: string }> => {
    const response = await api.post<
      ApiResponse<{ veterinaria: Veterinaria; linkPagoKit: string }>
    >('/admin/veterinarias', data);
    return response.data.data;
  },

  subirLogo: async (
    archivo: File,
    veterinariaId?: string
  ): Promise<LogoInfo> => {
    const formData = new FormData();
    formData.append('logo', archivo);
    if (veterinariaId) {
      formData.append('veterinariaId', veterinariaId);
    }

    const response = await api.post<ApiResponse<{ logo: LogoInfo }>>(
      '/admin/veterinarias/upload-logo',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data.logo;
  },

  actualizar: async (
    id: string,
    data: Partial<CrearVeterinariaInput>
  ): Promise<Veterinaria> => {
    const response = await api.patch<ApiResponse<Veterinaria>>(
      `/admin/veterinarias/${id}`,
      data
    );
    return response.data.data;
  },

  marcarGratis: async (
    id: string,
    esGratis: boolean,
    motivoGratis?: string
  ): Promise<Veterinaria> => {
    const response = await api.patch<ApiResponse<Veterinaria>>(
      `/admin/veterinarias/${id}/gratis`,
      { esGratis, motivoGratis }
    );
    return response.data.data;
  },

  marcarKitPagado: async (
    id: string,
    kitPagado: boolean
  ): Promise<Veterinaria> => {
    const response = await api.patch<ApiResponse<Veterinaria>>(
      `/admin/veterinarias/${id}/kit-pagado`,
      { kitPagado }
    );
    return response.data.data;
  },

  activar: async (id: string): Promise<Veterinaria> => {
    const response = await api.patch<ApiResponse<Veterinaria>>(
      `/admin/veterinarias/${id}/activar`
    );
    return response.data.data;
  },

  desactivar: async (id: string): Promise<Veterinaria> => {
    const response = await api.patch<ApiResponse<Veterinaria>>(
      `/admin/veterinarias/${id}/desactivar`
    );
    return response.data.data;
  },

  generarNuevoLinkPago: async (id: string): Promise<{ linkPago: string }> => {
    const response = await api.post<ApiResponse<{ linkPago: string }>>(
      `/admin/veterinarias/${id}/link-pago`
    );
    return response.data.data;
  },

  restablecerPassword: async (id: string, passwordNuevo: string): Promise<void> => {
    await api.patch(`/admin/veterinarias/${id}/restablecer-password`, { passwordNuevo });
  },

  toggleVisibilidadDirectorio: async (
    id: string,
    visibleEnDirectorio: boolean
  ): Promise<Veterinaria> => {
    const response = await api.patch<ApiResponse<Veterinaria>>(
      `/admin/veterinarias/${id}`,
      { visibleEnDirectorio }
    );
    return response.data.data;
  },
};

export default veterinariasServicio;
